import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import PhoneVerification from './PhoneVerification';
import ForgotPassword from './ForgotPassword';
import './AuthPage.css';

type AuthMode = 'login' | 'register' | 'verify' | 'forgot';

const AuthPage: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<AuthMode>('login');
  const [phoneForVerification, setPhoneForVerification] = useState('');
  const [initialized, setInitialized] = useState(false);

  // On mount: handle unverified sessions
  useEffect(() => {
    // If user is authenticated but not verified, and we're not continuing verification
    const continueVerify = searchParams.get('continue') === 'true';
    
    if (isAuthenticated && user && !user.is_verified && !continueVerify) {
      // Log out stale unverified session - user clicked Login fresh
      logout();
    }
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - intentionally not including deps

  // Don't render until initialized
  if (!initialized) {
    return null;
  }

  // Redirect if already authenticated and verified
  if (isAuthenticated && user?.is_verified) {
    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
  }

  // Show verification if authenticated but not verified (only after registration)
  if (isAuthenticated && user && !user.is_verified && mode !== 'verify') {
    setMode('verify');
    setPhoneForVerification(user.phone_number);
  }

  const handleLoginSuccess = () => {
    // Will be handled by the redirect logic above
  };

  const handleRegisterSuccess = (phone: string) => {
    setPhoneForVerification(phone);
    setMode('verify');
  };

  const handleVerificationSuccess = () => {
    // Redirect will happen automatically due to auth context update
  };

  const handleNeedsVerification = (phone: string) => {
    setPhoneForVerification(phone);
    setMode('verify');
  };

  const handleBackToLogin = () => {
    // Log out to clear the unverified session, then show login
    logout();
    setMode('login');
    setPhoneForVerification('');
  };

  const handleForgotPassword = () => {
    setMode('forgot');
  };

  const handleForgotPasswordSuccess = () => {
    setMode('login');
  };

  return (
    <div className="auth-page">
      {/* Navigation Bar */}
      <nav className="auth-navbar">
        <div className="auth-navbar-content">
          <div className="auth-navbar-logo" onClick={() => navigate('/')}>
            <img src="/genlend-logo.png" alt="GenLend" className="auth-navbar-logo-icon" style={{ width: '52px', height: '52px', borderRadius: '50%' }} />
            <span className="auth-navbar-logo-text">GenLend</span>
          </div>
          
          <div className="auth-navbar-actions">
            <button 
              className="auth-navbar-back-btn"
              onClick={() => navigate('/')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Home
            </button>
          </div>
        </div>
      </nav>

      <div className="brand-accent-bar"></div>
      <div className="auth-container">
        <div className="auth-branding">
          <div className="auth-hero-text">
            <img src="/genlend-logo.png" alt="GenLend" className="auth-hero-logo" />
            <h1 className="auth-hero-title">
              GenLend
            </h1>
            <p className="auth-hero-subtitle">
              Smart Loans for Bright Futures
            </p>
          </div>
          
          <div className="auth-hero-badges">
            <div className="auth-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="auth-badge-content">
                <strong>Quick Approval</strong>
                <span>Get approved within 24 hours</span>
              </div>
            </div>
            <div className="auth-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1V23M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6312 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6312 13.6815 18 14.5717 18 15.5C18 16.4283 17.6312 17.3185 16.9749 17.9749C16.3185 18.6312 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="auth-badge-content">
                <strong>R200 - R750</strong>
                <span>Flexible loan amounts</span>
              </div>
            </div>
            <div className="auth-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 10V6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V10C2 11.1 2.9 12 4 12H20C21.1 12 22 11.1 22 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 10L12 16L22 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="auth-badge-content">
                <strong>NSFAS Students</strong>
                <span>Designed for beneficiaries</span>
              </div>
            </div>
            <div className="auth-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22S2 16 2 10V5L12 2L22 5V10C22 16 12 16 12 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="auth-badge-content">
                <strong>Secure & Safe</strong>
                <span>Your data is protected</span>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-form-container">
          {mode === 'login' && (
            <LoginForm
              onSuccess={handleLoginSuccess}
              onSwitchToRegister={() => setMode('register')}
              onNeedsVerification={handleNeedsVerification}
              onForgotPassword={handleForgotPassword}
            />
          )}

          {mode === 'forgot' && (
            <ForgotPassword
              onBack={() => setMode('login')}
              onSuccess={handleForgotPasswordSuccess}
            />
          )}

          {mode === 'register' && (
            <RegisterForm
              onSuccess={handleRegisterSuccess}
              onSwitchToLogin={() => setMode('login')}
            />
          )}

          {mode === 'verify' && phoneForVerification && (
            <PhoneVerification
              phone={phoneForVerification}
              onSuccess={handleVerificationSuccess}
              onBack={handleBackToLogin}
            />
          )}
        </div>
      </div>

      <div className="auth-footer">
        <div className="footer-content">
          <p>&copy; 2026 GenLend. All rights reserved.</p>
          <div className="footer-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/contact">Contact Us</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;


